import { LegalShell, Section, DataOwnershipBlock, ProviderDisclaimer } from "@/components/legal/LegalShell";

export default function PrivacyPolicy() {
  return (
    <LegalShell title="Privacy Policy">
      <p>
        This Privacy Policy explains how <strong>RecoverOS</strong>, operated by <strong>Trustera Services</strong>,
        New Delhi, India ("RecoverOS", "we", "us"), collects, uses, stores and protects information when you use our
        receivables, collections and settlement management platform. This policy is aligned with the principles of
        India's <strong>Digital Personal Data Protection Act, 2023 (DPDP Act)</strong>.
      </p>

      <ProviderDisclaimer />

      <Section title="1. Information We Collect">
        <ul className="space-y-1.5 list-disc list-inside">
          <li><strong>Tenant &amp; account data:</strong> organization name, industry, contact details and subscription information.</li>
          <li><strong>User data:</strong> name, work email, role and authentication credentials (passwords are stored only as salted hashes).</li>
          <li><strong>Customer/borrower data entered by the tenant:</strong> account, loan, payment, settlement, follow-up and activity records uploaded or created by the tenant within their workspace.</li>
          <li><strong>Usage &amp; security data:</strong> login events, audit logs, IP/device metadata and timestamps used for security and reliability.</li>
        </ul>
      </Section>

      <Section title="2. Purpose of Collection">
        <p>We process data only to deliver and operate the service, including: authenticating users, providing role-based access, enabling recovery workflows, generating reports the tenant requests, maintaining audit trails, ensuring security, and providing customer support. We use data minimization and purpose limitation — we do not use personal data for purposes incompatible with the above.</p>
      </Section>

      <Section title="3. Data Retention">
        <p>Tenant data is retained for as long as the subscription is active. After termination, data is retained for <strong>90 days</strong> to allow the tenant to export it, after which it is <strong>permanently deleted or anonymized</strong>, unless a longer period is required by law.</p>
      </Section>

      <Section title="4. Security Measures">
        <ul className="space-y-1.5 list-disc list-inside">
          <li>Encrypted data transfer over HTTPS/TLS</li>
          <li>Strict multi-tenant isolation — every query is filtered by tenant</li>
          <li>Role-based access control (Admin / Manager / Agent)</li>
          <li>Salted password hashing; no plain-text passwords</li>
          <li>Immutable audit logs and rate-limited authentication</li>
        </ul>
        <p>See our <a href="/security" className="text-[#0B3D91] font-semibold underline">Security page</a> for details.</p>
      </Section>

      <Section title="5. Data Ownership">
        <DataOwnershipBlock />
      </Section>

      <Section title="6. Your Rights (DPDP Principles)">
        <p>Subject to applicable law, data principals may request access to, correction of, or erasure of their personal data, and may withdraw consent. Tenants act as the Data Fiduciary for the customer data they upload; RecoverOS acts as a Data Processor on the tenant's instructions. Requests relating to customer data should be directed to the relevant tenant; requests relating to your RecoverOS user account may be sent to us.</p>
      </Section>

      <Section title="7. Grievance &amp; Contact">
        <p>For privacy questions, data requests, or grievances, contact our team:</p>
        <ul className="space-y-1.5 list-disc list-inside">
          <li>Email: <a href="mailto:support@recoveros.in" className="text-[#0B3D91] font-semibold">support@recoveros.in</a></li>
          <li>Phone: +91 78400 52502</li>
          <li>Address: Trustera Services, New Delhi, India</li>
        </ul>
      </Section>
    </LegalShell>
  );
}
